import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { inspectionAPI, vehicleAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
    Camera, Truck, Gauge, Check, Loader2, Upload, 
    ChevronRight, ChevronLeft, CheckCircle2, AlertCircle,
    RefreshCw, MapPin
} from 'lucide-react';
import { toast } from 'sonner';

const PHOTO_STEPS = [
    { type: 'front', label: 'Front View', instruction: 'Take a photo of the front of the vehicle' },
    { type: 'back', label: 'Rear View', instruction: 'Take a photo of the rear of the vehicle' },
    { type: 'left', label: 'Left Side', instruction: 'Take a photo of the left side' },
    { type: 'right', label: 'Right Side', instruction: 'Take a photo of the right side' },
    { type: 'interior_front', label: 'Interior Front', instruction: 'Take a photo of the control panel/dashboard' },
    { type: 'interior_back', label: 'Interior Back', instruction: 'Take a photo of the load cabin/cargo area' }
];

export default function DriverCheckInPage() {
    const { user } = useAuth();
    const { t, language } = useLanguage();
    const navigate = useNavigate();
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [step, setStep] = useState('select'); // select, odometer, photos, complete
    const [selectedVehicle, setSelectedVehicle] = useState('');
    const [odometer, setOdometer] = useState('');
    const [notes, setNotes] = useState('');
    const [inspectionId, setInspectionId] = useState(null);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    const [uploadedPhotos, setUploadedPhotos] = useState({});
    const [uploading, setUploading] = useState(false);
    const [existingInspection, setExistingInspection] = useState(null);
    const [currentLocation, setCurrentLocation] = useState(null);
    const fileInputRef = useRef(null);
    
    // Translated photo steps
    const getPhotoSteps = () => [
        { type: 'front', label: t('checkIn.exteriorFront'), instruction: language === 'el' ? 'Τραβήξτε φωτογραφία του μπροστινού μέρους' : 'Take a photo of the front of the vehicle' },
        { type: 'back', label: t('checkIn.exteriorBack'), instruction: language === 'el' ? 'Τραβήξτε φωτογραφία του πίσω μέρους' : 'Take a photo of the rear of the vehicle' },
        { type: 'left', label: t('checkIn.exteriorLeft'), instruction: language === 'el' ? 'Τραβήξτε φωτογραφία της αριστερής πλευράς' : 'Take a photo of the left side' },
        { type: 'right', label: t('checkIn.exteriorRight'), instruction: language === 'el' ? 'Τραβήξτε φωτογραφία της δεξιάς πλευράς' : 'Take a photo of the right side' },
        { type: 'interior_front', label: t('checkIn.interiorFront'), instruction: language === 'el' ? 'Τραβήξτε φωτογραφία του ταμπλό' : 'Take a photo of the control panel/dashboard' },
        { type: 'interior_back', label: t('checkIn.interiorBack'), instruction: language === 'el' ? 'Τραβήξτε φωτογραφία του χώρου φορτίου' : 'Take a photo of the load cabin/cargo area' }
    ];

    useEffect(() => {
        fetchData();
        // Get GPS location on page load
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setCurrentLocation({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        accuracy: position.coords.accuracy
                    });
                },
                (error) => {
                    console.log('GPS not available:', error.message);
                },
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
            );
        }
    }, []);

    const fetchData = async () => {
        try {
            const [vehiclesRes, todayRes] = await Promise.all([
                vehicleAPI.getAll(),
                inspectionAPI.getMyToday()
            ]);
            setVehicles(vehiclesRes.data.filter(v => v.status === 'active'));
            
            if (todayRes.data) {
                setExistingInspection(todayRes.data);
                setInspectionId(todayRes.data.id);
                setSelectedVehicle(todayRes.data.vehicle_id);
                setOdometer(todayRes.data.odometer_reading);
                
                // Mark uploaded photos
                const uploaded = {};
                todayRes.data.photos?.forEach(p => {
                    uploaded[p.photo_type] = true;
                });
                setUploadedPhotos(uploaded);
                
                // Determine current step
                const uploadedCount = Object.keys(uploaded).length;
                if (uploadedCount === 6) {
                    setStep('complete');
                } else if (uploadedCount > 0 || todayRes.data.id) {
                    setStep('photos');
                    // Find first unuploaded photo
                    const firstUnuploaded = PHOTO_STEPS.findIndex(s => !uploaded[s.type]);
                    setCurrentPhotoIndex(firstUnuploaded >= 0 ? firstUnuploaded : 0);
                }
            }
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleStartInspection = async () => {
        if (!selectedVehicle) {
            toast.error('Please select a vehicle');
            return;
        }
        setStep('odometer');
    };

    const handleSubmitOdometer = async () => {
        if (!odometer || parseFloat(odometer) <= 0) {
            toast.error('Please enter a valid odometer reading');
            return;
        }

        setUploading(true);
        try {
            const inspectionData = {
                vehicle_id: selectedVehicle,
                odometer_reading: parseFloat(odometer),
                notes: notes || null
            };
            
            // Add location if available
            if (currentLocation) {
                inspectionData.checkin_location = currentLocation;
            }
            
            const response = await inspectionAPI.create(inspectionData);
            setInspectionId(response.data.id);
            setStep('photos');
            toast.success('Inspection started. Now upload photos.');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create inspection');
        } finally {
            setUploading(false);
        }
    };

    const handlePhotoCapture = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const currentPhoto = PHOTO_STEPS[currentPhotoIndex];
        setUploading(true);

        try {
            await inspectionAPI.uploadPhoto(inspectionId, currentPhoto.type, file);
            
            const newUploaded = { ...uploadedPhotos, [currentPhoto.type]: true };
            setUploadedPhotos(newUploaded);
            
            toast.success(`${currentPhoto.label} uploaded!`);

            // Move to next photo or complete
            if (currentPhotoIndex < PHOTO_STEPS.length - 1) {
                // Find next unuploaded
                let nextIndex = currentPhotoIndex + 1;
                while (nextIndex < PHOTO_STEPS.length && newUploaded[PHOTO_STEPS[nextIndex].type]) {
                    nextIndex++;
                }
                if (nextIndex < PHOTO_STEPS.length) {
                    setCurrentPhotoIndex(nextIndex);
                } else {
                    setStep('complete');
                }
            } else {
                setStep('complete');
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to upload photo');
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const getVehicleInfo = (vehicleId) => {
        const vehicle = vehicles.find(v => v.id === vehicleId);
        return vehicle ? `${vehicle.make} ${vehicle.model} (${vehicle.license_plate})` : '';
    };

    const uploadedCount = Object.keys(uploadedPhotos).length;

    if (loading) {
        return (
            <div className="min-h-[80vh] flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
            </div>
        );
    }

    return (
        <div className="max-w-lg mx-auto px-2 sm:px-0 pb-20" data-testid="driver-checkin-page">
            {/* Progress Header */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <h1 className="text-xl font-bold text-slate-800">Daily Check-In</h1>
                    {step !== 'select' && step !== 'complete' && (
                        <span className="text-sm text-zinc-500">
                            {step === 'odometer' ? 'Step 1 of 2' : `Photos: ${uploadedCount}/6`}
                        </span>
                    )}
                </div>
                
                {/* Progress Bar */}
                <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div 
                        className="h-full bg-gradient-to-r from-[#1B7EB5] via-[#58A6B4] to-[#8CC774] transition-all duration-300"
                        style={{ 
                            width: step === 'select' ? '0%' : 
                                   step === 'odometer' ? '20%' : 
                                   step === 'complete' ? '100%' :
                                   `${20 + (uploadedCount / 6) * 80}%` 
                        }}
                    />
                </div>
            </div>

            {/* Step: Select Vehicle */}
            {step === 'select' && (
                <div className="space-y-6 animate-fade-in">
                    {existingInspection ? (
                        <div className="fleet-card p-4 border-[#1B7EB5]/30">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 bg-[#1B7EB5]/10 rounded-full flex items-center justify-center">
                                    <AlertCircle className="w-5 h-5 text-[#1B7EB5]" />
                                </div>
                                <div>
                                    <p className="text-white font-medium">Continue Inspection</p>
                                    <p className="text-sm text-zinc-500">You have an unfinished inspection</p>
                                </div>
                            </div>
                            <p className="text-zinc-400 text-sm mb-4">
                                Vehicle: {existingInspection.vehicle_info}<br/>
                                Photos uploaded: {uploadedCount}/6
                            </p>
                            <Button
                                onClick={() => setStep('photos')}
                                className="w-full bg-[#1B7EB5] hover:bg-[#58A6B4] text-white"
                            >
                                Continue Inspection
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div className="fleet-card p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-12 h-12 bg-[#1B7EB5]/10 rounded-full flex items-center justify-center">
                                        <Truck className="w-6 h-6 text-[#1B7EB5]" />
                                    </div>
                                    <div>
                                        <p className="text-white font-medium">Select Your Vehicle</p>
                                        <p className="text-sm text-zinc-500">Choose the vehicle you will drive today</p>
                                    </div>
                                </div>

                                <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
                                    <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="vehicle-select">
                                        <SelectValue placeholder="Select a vehicle" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white border-slate-200">
                                        {vehicles.map(vehicle => (
                                            <SelectItem key={vehicle.id} value={vehicle.id} className="py-3">
                                                <div>
                                                    <p className="font-medium">{vehicle.make} {vehicle.model}</p>
                                                    <p className="text-xs text-zinc-500">{vehicle.license_plate} • {vehicle.current_mileage?.toLocaleString()} km</p>
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <Button
                                onClick={handleStartInspection}
                                disabled={!selectedVehicle}
                                className="w-full bg-gradient-to-r from-[#1B7EB5] via-[#58A6B4] to-[#8CC774] text-white h-16 text-lg font-semibold rounded-xl shadow-lg active:scale-[0.98] transition-transform"
                                data-testid="start-inspection-btn"
                            >
                                Continue
                                <ChevronRight className="w-5 h-5 ml-2" />
                            </Button>
                        </>
                    )}
                </div>
            )}

            {/* Step: Odometer */}
            {step === 'odometer' && (
                <div className="space-y-6 animate-fade-in">
                    <div className="fleet-card p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center">
                                <Gauge className="w-6 h-6 text-green-500" />
                            </div>
                            <div>
                                <p className="text-white font-medium">Record Odometer</p>
                                <p className="text-sm text-zinc-500">{getVehicleInfo(selectedVehicle)}</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="form-group">
                                <Label className="form-label">Current Odometer Reading (km)</Label>
                                <Input
                                    type="number"
                                    value={odometer}
                                    onChange={(e) => setOdometer(e.target.value)}
                                    className="bg-white border-slate-300 text-slate-900 h-14 text-xl text-center"
                                    placeholder="e.g., 15000"
                                    min="0"
                                    data-testid="odometer-input"
                                />
                            </div>

                            <div className="form-group">
                                <Label className="form-label">Notes (Optional)</Label>
                                <Textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    className="bg-white border-slate-300 text-slate-900"
                                    placeholder="Any issues or notes..."
                                    rows={3}
                                    data-testid="notes-input"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <Button
                            variant="outline"
                            onClick={() => setStep('select')}
                            className="flex-1 border-zinc-700 text-zinc-400 h-14 rounded-xl active:scale-[0.98] transition-transform"
                        >
                            <ChevronLeft className="w-4 h-4 mr-2" />
                            Back
                        </Button>
                        <Button
                            onClick={handleSubmitOdometer}
                            disabled={uploading || !odometer}
                            className="flex-1 bg-gradient-to-r from-[#1B7EB5] to-[#8CC774] text-white h-14 font-semibold rounded-xl active:scale-[0.98] transition-transform"
                            data-testid="submit-odometer-btn"
                        >
                            {uploading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <>
                                    Continue
                                    <ChevronRight className="w-5 h-5 ml-2" />
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            )}

            {/* Step: Photos */}
            {step === 'photos' && (
                <div className="space-y-4 animate-fade-in">
                    {/* Photo Progress Grid - Mobile optimized */}
                    <div className="grid grid-cols-6 gap-2 mb-4">
                        {PHOTO_STEPS.map((photo, index) => (
                            <button 
                                key={photo.type}
                                onClick={() => setCurrentPhotoIndex(index)}
                                className={`aspect-square rounded-xl flex items-center justify-center text-xs font-medium transition-all active:scale-95 ${
                                    uploadedPhotos[photo.type] 
                                        ? 'bg-[#8CC774]/20 text-[#8CC774] border-2 border-[#8CC774]/50' 
                                        : index === currentPhotoIndex 
                                            ? 'bg-[#1B7EB5]/20 text-[#1B7EB5] ring-2 ring-[#1B7EB5]' 
                                            : 'bg-zinc-800 text-zinc-500 border border-zinc-700'
                                }`}
                            >
                                {uploadedPhotos[photo.type] ? (
                                    <Check className="w-5 h-5" />
                                ) : (
                                    index + 1
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Current Photo Step */}
                    <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-6">
                        <div className="text-center">
                            <div className="w-24 h-24 bg-[#1B7EB5]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <Camera className="w-12 h-12 text-[#1B7EB5]" />
                            </div>
                            <h2 className="text-2xl font-bold text-slate-800 mb-2">
                                {PHOTO_STEPS[currentPhotoIndex].label}
                            </h2>
                            <p className="text-zinc-400 mb-6 text-sm">
                                {PHOTO_STEPS[currentPhotoIndex].instruction}
                            </p>

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                capture="environment"
                                onChange={handleFileSelect}
                                className="hidden"
                                data-testid="photo-input"
                            />

                            <Button
                                onClick={handlePhotoCapture}
                                disabled={uploading}
                                className="w-full bg-gradient-to-r from-[#1B7EB5] via-[#58A6B4] to-[#8CC774] text-white h-20 text-xl font-semibold rounded-2xl shadow-lg active:scale-[0.98] transition-transform"
                                data-testid="capture-photo-btn"
                            >
                                {uploading ? (
                                    <>
                                        <Loader2 className="w-7 h-7 mr-3 animate-spin" />
                                        Uploading...
                                    </>
                                ) : (
                                    <>
                                        <Camera className="w-7 h-7 mr-3" />
                                        Take Photo
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* Navigation */}
                    <div className="flex gap-3">
                        <Button
                            variant="outline"
                            onClick={() => setCurrentPhotoIndex(Math.max(0, currentPhotoIndex - 1))}
                            disabled={currentPhotoIndex === 0}
                            className="flex-1 border-zinc-700 text-zinc-400 h-14 rounded-xl active:scale-[0.98] transition-transform"
                        >
                            <ChevronLeft className="w-5 h-5 mr-1" />
                            Previous
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setCurrentPhotoIndex(Math.min(PHOTO_STEPS.length - 1, currentPhotoIndex + 1))}
                            disabled={currentPhotoIndex === PHOTO_STEPS.length - 1}
                            className="flex-1 border-zinc-700 text-zinc-400 h-14 rounded-xl active:scale-[0.98] transition-transform"
                        >
                            Next
                            <ChevronRight className="w-5 h-5 ml-1" />
                        </Button>
                    </div>

                    {uploadedCount === 6 && (
                        <Button
                            onClick={() => setStep('complete')}
                            className="w-full bg-[#8CC774] hover:bg-[#7db866] text-white h-16 text-lg font-semibold rounded-xl shadow-lg active:scale-[0.98] transition-transform"
                        >
                            <Check className="w-6 h-6 mr-2" />
                            Complete Inspection
                        </Button>
                    )}
                </div>
            )}

            {/* Step: Complete */}
            {step === 'complete' && (
                <div className="text-center space-y-6 animate-fade-in">
                    <div className="w-28 h-28 bg-[#8CC774]/20 rounded-full flex items-center justify-center mx-auto">
                        <CheckCircle2 className="w-14 h-14 text-[#8CC774]" />
                    </div>
                    
                    <div>
                        <h2 className="text-2xl font-bold text-slate-800 mb-2">
                            {language === 'el' ? 'Check-In Ολοκληρώθηκε!' : 'Check-In Complete!'}
                        </h2>
                        <p className="text-zinc-400">
                            {language === 'el' 
                                ? 'Η ημερήσια επιθεώρηση υποβλήθηκε επιτυχώς.' 
                                : 'Your daily inspection has been submitted successfully.'}
                        </p>
                    </div>

                    <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-5 text-left">
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <span className="text-zinc-500">{language === 'el' ? 'Όχημα' : 'Vehicle'}</span>
                                <span className="text-white font-medium">{getVehicleInfo(selectedVehicle)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-zinc-500">{language === 'el' ? 'Χιλιόμετρα' : 'Odometer'}</span>
                                <span className="text-[#1B7EB5] font-semibold text-lg">{parseFloat(odometer)?.toLocaleString()} km</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-zinc-500">{language === 'el' ? 'Φωτογραφίες' : 'Photos'}</span>
                                <span className="text-[#8CC774] font-medium">{uploadedCount}/6 {language === 'el' ? 'ανεβασμένες' : 'uploaded'}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-zinc-500">{language === 'el' ? 'Κατάσταση' : 'Status'}</span>
                                <span className="px-3 py-1 bg-[#58A6B4]/20 text-[#58A6B4] rounded-full text-sm font-medium">
                                    {language === 'el' ? 'Αναμονή Ελέγχου' : 'Pending Review'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <p className="text-sm text-zinc-500 px-4">
                        {language === 'el' 
                            ? 'Ο διαχειριστής στόλου θα ελέγξει αυτήν την επιθεώρηση σύντομα. Μπορείτε τώρα να κάνετε Check-Out.' 
                            : 'Your fleet manager will review this inspection shortly. You can now do Check-Out.'}
                    </p>
                    
                    <Button
                        onClick={() => navigate('/driver')}
                        className="w-full bg-gradient-to-r from-[#1B7EB5] to-[#58A6B4] hover:from-[#165f8c] hover:to-[#4a8f99] text-white font-semibold py-6 rounded-xl"
                    >
                        {language === 'el' ? 'Επιστροφή στην Αρχική' : 'Return to Dashboard'}
                        <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                </div>
            )}
        </div>
    );
}
